import { useEffect, useRef, useState } from 'react';
import APIClient from '../api/api';
import { GetNotificationsResponse } from '../api/models';

export type ApiHealthStatus = 'connecting' | 'connected' | 'error';

export interface ApiHealth {
  status: ApiHealthStatus;
  latencyMs: number | null;
  lastSyncAt: Date | null;
  notifications: GetNotificationsResponse | null;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Polls /v1/notifications on a fixed interval. Tracks request latency, last sync time,
 * and the notifications payload (used for sidebar badges and the status panel).
 */
export function useApiHealth(): ApiHealth {
  const [health, setHealth] = useState<ApiHealth>({
    status: 'connecting',
    latencyMs: null,
    lastSyncAt: null,
    notifications: null,
  });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    async function tick() {
      const start = performance.now();
      try {
        const notifications = await APIClient.getNotifications();
        if (cancelledRef.current) return;
        setHealth({
          status: 'connected',
          latencyMs: Math.round(performance.now() - start),
          lastSyncAt: new Date(),
          notifications,
        });
      } catch {
        if (cancelledRef.current) return;
        setHealth((prev) => ({
          ...prev,
          status: 'error',
          latencyMs: Math.round(performance.now() - start),
          lastSyncAt: new Date(),
        }));
      }
    }

    tick();
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
  }, []);

  return health;
}
